import { Document, Packer, Paragraph, TextRun, ExternalHyperlink, AlignmentType, BorderStyle } from 'docx';
import { buildResumeParagraphs } from './resumeRenderer';
import {
  cleanNumbersForDownload,
  isResumeHeader,
  isLinkedInLine,
  extractLinkedInUrl,
  getLocalizedDocLabel,
  sanitizeFilePart,
  extractNameFromContent,
  getContentByDocType,
} from './downloadUtils';

export async function downloadResume(format, resultItem, applicationInfo, generatedLanguage, language, result) {
  const item = resultItem || result;
  if (!item) return;

  const rawContent = getContentByDocType(item);
  if (typeof rawContent !== "string" || !rawContent.trim()) {
    alert("No content available to download");
    return;
  }

  const content = cleanNumbersForDownload(rawContent, item.docType);

  // ---- filename parts ----
  const name = sanitizeFilePart(extractNameFromContent(content));
  const role = sanitizeFilePart(applicationInfo?.targetJobTitle || "Role");

  const localizedDocType = sanitizeFilePart(
    getLocalizedDocLabel(item.docType, generatedLanguage || language)
  );

  const fileName = `${name}_${role}_${localizedDocType}.${format}`;

  if (format === "docx") {
    const lines = content.split("\n");
    const paragraphs = [];

    // ── Bio / LinkedIn / Portfolio: no resume-style header block ────────
    // These doc types do not start with a name + contact block.
    // The contact-parsing loop would silently eat bio content (lines with
    // metrics/percentages) or swallow narrative lines without @/digits/comma.
    // Pass all lines directly to buildResumeParagraphs.
    const isNarrativeType = ['bio', 'linkedin', 'portfolio'].includes(item.docType);

    if (isNarrativeType) {
      const bodyParagraphs = buildResumeParagraphs(lines, item);
      paragraphs.push(...bodyParagraphs);
    } else {
      // ── Header block: name + contact lines (resume / cv / coverLetter) ──
      // The first non-empty line is always the candidate name. Detected
      // positionally — never by keyword matching or casing.
      let bodyStart = 0;
      while (bodyStart < lines.length && !lines[bodyStart].trim()) bodyStart++;

      if (bodyStart < lines.length) {
        const nameLine = lines[bodyStart].trim();
        bodyStart++;

        // Candidate name — 28pt, bold, centered
        paragraphs.push(
          new Paragraph({
            style: "ResumeName",
            children: [new TextRun({ text: nameLine })]
          })
        );

        // Role subtitle — always from applicationInfo.targetJobTitle (resume/cv only).
        // Never inferred from AI output — title lines in the header are discarded below.
        const roleTitle = applicationInfo?.targetJobTitle?.trim();
        if (roleTitle && (item.docType === 'resume' || item.docType === 'cv')) {
          paragraphs.push(
            new Paragraph({
              style: "ResumeSubtitle",
              children: [new TextRun({ text: roleTitle })]
            })
          );
        }

        // Contact lines — collect all fields after the name until a blank line or section header.
        // Title lines (no @, no digits, no comma) are silently skipped — they belong in the Summary.
        // Plain fields (city/state, phone, email) are joined inline with • on one centered paragraph.
        // LinkedIn gets its own centered hyperlink line below.
        const contactFields = [];
        let linkedInUrl = null;

        while (
          bodyStart < lines.length &&
          lines[bodyStart].trim() &&
          !isResumeHeader(lines[bodyStart])
        ) {
          const cl = lines[bodyStart].trim();
          bodyStart++;

          if (isLinkedInLine(cl)) {
            if (!linkedInUrl) {
              const extracted = extractLinkedInUrl(cl);
              if (extracted) {
                linkedInUrl = extracted;
              } else {
                // Extraction failed (non-/in/ URL or unexpected format) — treat as
                // a contact field so the URL isn't silently dropped.
                contactFields.push(cl);
              }
            }
            continue;
          }

          // Skip title lines: pure words with no @, no digits, no comma (e.g. "Senior Marketing Director")
          if (!/@/.test(cl) && !/\d/.test(cl) && !/,/.test(cl)) continue;

          contactFields.push(cl);
        }

        if (contactFields.length > 0) {
          paragraphs.push(
            new Paragraph({
              style: "ResumeContact",
              children: [new TextRun({ text: contactFields.join(' • ') })]
            })
          );
        }

        if (linkedInUrl) {
          paragraphs.push(
            new Paragraph({
              style: "ResumeContact",
              children: [
                new ExternalHyperlink({
                  link: linkedInUrl,
                  children: [
                    new TextRun({
                      text: linkedInUrl,
                      color: "0563C1",
                      underline: {}
                    })
                  ]
                })
              ]
            })
          );
        }
      }

      // ── Body lines ─────────────────────────────────────────────────────
      const bodyParagraphs = buildResumeParagraphs(lines.slice(bodyStart), item);
      paragraphs.push(...bodyParagraphs);
    }


    const doc = new Document({
      styles: {
        default: {
          document: {
            run: { font: "Calibri", size: 22 }
          }
        },
        paragraphStyles: [
          {
            id: "ResumeName",
            name: "Resume Name",
            basedOn: "Normal",
            next: "ResumeContact",
            run: {
              font: "Calibri",
              size: 56,   // 28pt per Jessica's spec
              bold: true,
              color: "1A1A1A",
            },
            paragraph: {
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 80 },
            },
          },
          {
            id: "ResumeSubtitle",
            name: "Resume Subtitle",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 24,   // 12pt
              color: "555555",
            },
            paragraph: {
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 80 },
            },
          },
          {
            id: "ResumeContact",
            name: "Resume Contact",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 20,   // 10pt per Jessica's spec
              color: "1A1A1A",
            },
            paragraph: {
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 0, line: 240, lineRule: "exact" },
            },
          },
          {
            id: "ResumeHeading",
            name: "Resume Heading",
            basedOn: "Normal",
            next: "ResumeBody",
            run: {
              font: "Calibri",
              size: 22,   // 11pt per Jessica's spec
              bold: true,
              allCaps: true,
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 160, after: 80, line: 240, lineRule: "exact" },
              border: {
                bottom: {
                  color: "000000",
                  space: 1,
                  style: BorderStyle.SINGLE,
                  size: 6,
                },
              },
            },
          },
          {
            id: "ResumeCompany",
            name: "Resume Company",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 22,   // 11pt
              bold: true,
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 120, after: 0, line: 240, lineRule: "exact" },
            },
          },
          {
            id: "ResumeJobTitle",
            name: "Resume Job Title",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 22,   // 11pt
              italics: true,
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 0, after: 60, line: 240, lineRule: "exact" },
            },
          },
          {
            id: "ResumeScope",
            name: "Resume Scope",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 22,   // 11pt
              italics: true,
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 0, after: 40, line: 240, lineRule: "exact" },
            },
          },
          {
            id: "ResumeBody",
            name: "Resume Body",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 22,   // 11pt
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 0, after: 20, line: 240, lineRule: "exact" },
            },
          },
          {
            id: "ResumeBullet",
            name: "Resume Bullet",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 22,   // 11pt
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 0, after: 40, line: 240, lineRule: "exact" },
              indent: { left: 360, hanging: 180 },
            },
          },
          {
            id: "ResumeExperience",
            name: "Resume Experience",
            basedOn: "Normal",
            run: {
              font: "Calibri",
              size: 22,   // 11pt
              color: "1A1A1A",
            },
            paragraph: {
              spacing: { before: 0, after: 0, line: 240, lineRule: "exact" },
            },
          },
        ],
      },
      sections: [{
        properties: {
          page: {
            size: { width: 12240, height: 15840 },
            margin: { left: 1440, right: 1800, top: 1440, bottom: 1440 },
          },
        },
        children: paragraphs,
      }]
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);

  } else {
    const blob = new Blob([content], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}
